import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Layers, Info, Download, Map as MapIcon, X, Navigation, Route, Plus, Trash2, RefreshCw, Database } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { Button } from '@common/Button';
import { LoadingSpinner } from '@common/LoadingSpinner';
import { Toast } from '@common/Toast';
import { logError, logInfo } from '@utils/logger';
import html2canvas from 'html2canvas';
import { ItineraryRouteBuilder } from './ItineraryRouteBuilder';
import { LayerUpdateModal } from './LayerUpdateModal';
import { HauteursLibresModal } from './HauteursLibresModal';
import { getObjectsFromFirebase } from '../../../../utils/wfs/getObjectsFromFirebase';
import { db } from '../../../../config/firebase';
import * as turf from '@turf/turf';

// Get API key directly from environment variable
const GOOGLE_MAPS_API_KEY = 'AIzaSyAdNYCigSTxjhN3zL39QmB6H4nL5-zku7A';

// WMS Service URL
const WMS_URL = 'https://ws.mapserver.transports.gouv.qc.ca/swtq';

// WMS Layer configurations - Updated based on GetCapabilities
const WMS_LAYERS = [
  {
    id: 'pes_v_limtn_charg',
    wfsName: 'ms:pes_v_limtn_charg',
    name: 'Limites de charge', 
    color: '#EF4444', // Red
    enabled: false,
    description: 'Affiche les limites de charge sur les routes'
  },
  {
    id: 'aq_camion',
    wfsName: 'ms:aq_camion',
    name: 'Réseau de camionnage', 
    color: '#FBBF24', // Yellow
    enabled: false,
    description: 'Affiche le réseau de camionnage autorisé'
  },
  {
    id: 'evenements',
    wfsName: 'ms:evenements',
    name: 'Avertissements routiers', 
    color: '#F59E0B', // Amber
    enabled: false,
    description: 'Affiche les avertissements et travaux routiers'
  },
  {
    id: 'gsq_v_desc_strct_tri',
    wfsName: 'ms:gsq_v_desc_strct_tri',
    name: 'Structures', 
    color: '#6366F1', // Indigo
    enabled: false,
    description: 'Affiche les structures (ponts, viaducs, etc.)'
  },
  {
    id: 'camera',
    wfsName: 'ms:infos_cameras',
    name: 'Caméra de circulation',
    color: '#EC4899', // Pink
    enabled: false,
    description: 'Affiche les caméras de circulation routière'
  },
  {
    id: 'drainage',
    wfsName: 'ms:iit_v_drang_condu_permn_ligne',
    name: 'Élément de drainage',
    color: '#14B8A6', // Teal
    enabled: false,
    description: 'Affiche les éléments de drainage du réseau routier'
  },
  {
    id: 'condition_hivernale',
    wfsName: 'ms:conditions_routieres',
    name: 'Condition routière hivernale',
    color: '#8B5CF6', // Violet
    enabled: false,
    description: 'Affiche les conditions hivernales du réseau routier'
  },
  {
    id: 'pes_v_zone_degel',
    wfsName: 'ms:zone_degel',
    name: 'Zone de dégel',
    color: '#0EA5E9', // Sky
    enabled: false,
    description: 'Affiche les zones de dégel sur le réseau routier'
  },
  {
    id: 'pes_v_debit_circulation',
    wfsName: 'ms:circulation_routier',
    name: 'Débit de circulation',
    color: '#F97316', // Orange
    enabled: false,
    description: 'Affiche les débits de circulation sur le réseau routier'
  },
  {
    id: 'pes_v_port_installation',
    wfsName: 'ms:port_installation',
    name: 'Port - Installation',
    color: '#4ADE80', // Green
    enabled: false,
    description: 'Affiche les ports et installations maritimes'
  },
  {
    id: 'pes_v_travaux_routiers',
    wfsName: 'ms:chantiers_mtmdet',
    name: 'Travaux routiers',
    color: '#FB7185', // Rose
    enabled: false,
    description: 'Affiche les travaux routiers en cours'
  },
  {
    id: 'pes_v_repere_kilometrique',
    wfsName: 'ms:repere_km',
    name: 'Repère kilométrique',
    color: '#A3E635', // Lime
    enabled: false,
    description: 'Affiche les repères kilométriques du réseau routier'
  },
  {
    id: 'pes_v_reseau_ferroviaire',
    wfsName: 'ms:reseau_chfer_qc',
    name: 'Réseau ferroviaire',
    color: '#7C3AED', // Purple
    enabled: false,
    description: 'Affiche le réseau ferroviaire'
  },
  {
    id: 'pes_v_sortie_autoroute',
    wfsName: 'ms:sortie_aut',
    name: 'Sortie d\'autoroute',
    color: '#2DD4BF', // Emerald
    enabled: false,
    description: 'Affiche les sorties d\'autoroute'
  }
];

// Google Maps libraries to load
const libraries = ['places', 'drawing', 'geometry'];

const MTMDMapView = () => {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const wmsLayersRef = useRef({});
  const directionsServiceRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const routePolylineRef = useRef(null);
  const wfsMarkersRef = useRef([]);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showHauteursLibresModal, setShowHauteursLibresModal] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [layers, setLayers] = useState(WMS_LAYERS);
  const [showLegend, setShowLegend] = useState(false);
  const [showRouteBuilder, setShowRouteBuilder] = useState(false);
  const [currentRoute, setCurrentRoute] = useState(null);
  const [routeBuffer, setRouteBuffer] = useState(0.5); // 500m default buffer
  const [wfsFeatures, setWfsFeatures] = useState([]);
  const [displayMode, setDisplayMode] = useState('wms'); // 'wms' or 'wfs'
  const [wfsLayerVisibility, setWfsLayerVisibility] = useState({});
  const [toast, setToast] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [error, setError] = useState(null);
  
  // Use window.google directly instead of useJsApiLoader
  const [isLoaded, setIsLoaded] = useState(!!window.google?.maps);
  const [loadError, setLoadError] = useState(null);
  
  // Check if Google Maps is loaded
  useEffect(() => {
    if (window.google?.maps) {
      setIsLoaded(true);
    } else {
      // Wait for Google Maps to load from the main script
      const checkGoogleMaps = setInterval(() => {
        if (window.google?.maps) {
          setIsLoaded(true);
          clearInterval(checkGoogleMaps);
        }
      }, 100);
      
      // Set a timeout to prevent infinite checking
      setTimeout(() => {
        if (!window.google?.maps) {
          setLoadError(new Error('Google Maps failed to load'));
          clearInterval(checkGoogleMaps);
        }
      }, 10000);
      
      return () => clearInterval(checkGoogleMaps);
    }
  }, []);

  // Initialize Google Maps
  useEffect(() => {
    if (loadError) {
      setLoading(false);
      setError(`Google Maps failed to load: ${loadError.message || 'Unknown error'}. Please refresh the page and try again.`);
      return;
    }
    
    if (!isLoaded || !window.google?.maps) {
      logInfo('Google Maps not yet loaded, waiting...');
      return;
    }
    
    const initMap = async () => {
      try {
        if (!window.google || !window.google.maps) {
          throw new Error('Google Maps API not loaded');
        }

        // Create map instance
        const mapOptions = {
          center: { lat: 45.5017, lng: -73.5673 }, // Montreal
          zoom: 10,
          mapTypeId: google.maps.MapTypeId.ROADMAP,
          styles: [
            {
              featureType: "administrative",
              elementType: "all",
              stylers: [{ visibility: "on" }]
            },
            {
              featureType: "landscape",
              elementType: "all",
              stylers: [{ visibility: "on" }]
            },
            {
              featureType: "poi",
              elementType: "all", 
              stylers: [{ visibility: "on" }]
            },
            {
              featureType: "road",
              elementType: "all",
              stylers: [{ visibility: "on" }]
            },
            {
              featureType: "transit",
              elementType: "all",
              stylers: [{ visibility: "on" }]
            },
            {
              featureType: "water",
              elementType: "all",
              stylers: [{ visibility: "on" }]
            },
            {
              featureType: "all",
              elementType: "labels",
              stylers: [{ visibility: "on" }]
            }
          ],
          mapTypeControl: true,
          mapTypeControlOptions: {
            style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
            position: google.maps.ControlPosition.TOP_RIGHT
          },
          fullscreenControl: false
        };

        // Initialize directions service and renderer
        directionsServiceRef.current = new google.maps.DirectionsService();
        directionsRendererRef.current = new google.maps.DirectionsRenderer({
          suppressMarkers: false,
          polylineOptions: {
            strokeColor: '#3B82F6',
            strokeWeight: 5,
            strokeOpacity: 0.8
          }
        });

        // Try to restore previous map position from localStorage
        try {
          const savedPosition = localStorage.getItem('mtmdMapPosition');
          if (savedPosition) {
            const { center, zoom } = JSON.parse(savedPosition);
            mapOptions.center = center;
            mapOptions.zoom = zoom;
          }
        } catch (e) {
          // Ignore errors reading from localStorage
          console.warn('Failed to restore map position:', e);
        }

        googleMapRef.current = new google.maps.Map(mapRef.current, mapOptions);
        directionsRendererRef.current.setMap(googleMapRef.current);
        
        // Initialize WMS layers
        initializeWMSLayers();
        
        // Add event listener to save map position
        googleMapRef.current.addListener('idle', saveMapPosition);
        
        setMapLoaded(true);
        logInfo('MTMD Map initialized successfully');
      } catch (error) {
        logError('Failed to initialize map:', error);
        setError(`Failed to initialize map: ${error.message}. Please check your internet connection and try again.`);
      } finally {
        setLoading(false);
      }
    };

    initMap();

    return () => {
      // Save map position when component unmounts
      saveMapPosition();
    };
  }, [isLoaded, loadError]);

  // Initialize WMS layers
  const initializeWMSLayers = () => {
    if (!googleMapRef.current) return;

    // Clear existing layers
    Object.values(wmsLayersRef.current).forEach(layer => {
      if (layer && layer.setMap) {
        layer.setMap(null);
      }
    });

    // Initialize new layers
    layers.forEach(layer => {
      // Only create the layer object, don't attach to map yet
      const wmsLayer = createWMSLayer(layer.id, null);
      wmsLayersRef.current[layer.id] = wmsLayer;
      
      // Set map for enabled layers
      if (layer.enabled) {
        wmsLayer.setMap(googleMapRef.current);
      }
    });
  };

  // Create a WMS layer
  const createWMSLayer = (layerId, map) => {
    const wmsUrl = new URL(WMS_URL);
    // Base parameters - will be used as a template
    const baseParams = {
      'service': 'WMS',
      'version': '1.1.1',
      'request': 'GetMap',
      'layers': layerId,
      'styles': '',
      'format': 'image/png',
      'transparent': 'true'
    };
    
    // Create custom overlay
    class WMSOverlay extends google.maps.OverlayView {
      constructor(map) {
        super();
        this.map = map;
        this.name = layerId;
        this.div = null;
        if (map) this.setMap(map);
      }

      onAdd() {
        const div = document.createElement('div');
        div.style.position = 'absolute';
        div.style.width = '100%';
        div.style.height = '100%';
        div.style.pointerEvents = 'none';
        this.div = div;
        const panes = this.getPanes();
        panes.overlayLayer.appendChild(div);
      }

      draw() {
        if (!this.div) return;
        
        const overlayProjection = this.getProjection();
        if (!overlayProjection) return;

        const bounds = this.map.getBounds();
        if (!bounds) return;

        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        
        const mapWidth = this.map.getDiv().offsetWidth;
        const mapHeight = this.map.getDiv().offsetHeight;
        
        // Create a new URL for this specific request
        try {
          const wmsRequestUrl = new URL(WMS_URL);
          
          // Add base parameters
          Object.entries(baseParams).forEach(([key, value]) => {
            wmsRequestUrl.searchParams.set(key, value);
          });
          
          // Add dynamic parameters
          wmsRequestUrl.searchParams.set('width', mapWidth.toString());
          wmsRequestUrl.searchParams.set('height', mapHeight.toString());
          // Use lng,lat order for WMS 1.1.1 (instead of lat,lng for 1.3.0)
          wmsRequestUrl.searchParams.set('bbox', `${sw.lng()},${sw.lat()},${ne.lng()},${ne.lat()}`);
          wmsRequestUrl.searchParams.set('srs', 'EPSG:4326'); // Use 'srs' instead of 'crs' for 1.1.1
        
          // Create image element
          const img = document.createElement('img');
          img.src = wmsRequestUrl.toString();
          img.style.width = '100%';
          img.style.height = '100%';
          img.style.position = 'absolute';
          img.style.left = '0';
          img.style.top = '0';
          img.crossOrigin = 'anonymous'; // Add crossOrigin attribute
          img.onerror = () => {
            logError('Failed to load WMS layer:', wmsRequestUrl.toString());
            // Limit toast messages to avoid flooding
            if (Math.random() < 0.05) { // Only show error toast 5% of the time
              setToast({
                type: 'error',
                message: `Échec du chargement de la couche ${layerId}`
              });
            }
          };
          
          // Clear previous content
          this.div.innerHTML = '';
          this.div.appendChild(img);
          
          // Position the div
          const sw_pixel = overlayProjection.fromLatLngToDivPixel(sw);
          const ne_pixel = overlayProjection.fromLatLngToDivPixel(ne);
          
          this.div.style.left = sw_pixel.x + 'px';
          this.div.style.top = ne_pixel.y + 'px';
          this.div.style.width = (ne_pixel.x - sw_pixel.x) + 'px';
          this.div.style.height = (sw_pixel.y - ne_pixel.y) + 'px';
        } catch (error) {
          logError('Error creating WMS layer:', error);
          if (this.div) {
            this.div.innerHTML = '';
          }
        }
      }

      onRemove() {
        if (this.div && this.div.parentNode) {
          this.div.parentNode.removeChild(this.div);
          this.div = null;
        }
      }
    }

    return new WMSOverlay(map);
  };

  // Toggle a layer
  const toggleLayer = (layerId) => {
    setLayers(prevLayers => {
      // Update WFS layer visibility if in WFS mode
      if (displayMode === 'wfs') {
        setWfsLayerVisibility(prev => ({
          ...prev,
          [layerId]: !prev[layerId]
        }));
        
        // Update visibility of WFS markers
        wfsMarkersRef.current.forEach(marker => {
          if (marker.layerId === layerId) {
            marker.setMap(wfsLayerVisibility[layerId] ? null : googleMapRef.current);
          }
        });
      }
      
      const newLayers = prevLayers.map(layer => {
        if (layer.id === layerId) {
          const newLayer = { ...layer, enabled: !layer.enabled };
          
          // Update layer visibility
          const wmsLayer = wmsLayersRef.current[layerId];
          if (wmsLayer) {
            if (newLayer.enabled) {
              wmsLayer.setMap(googleMapRef.current);
            } else {
              wmsLayer.setMap(null);
            }
          }
          
          return newLayer;
        }
        return layer;
      });
      return newLayers;
    });
  };

  // Save map position to localStorage
  const saveMapPosition = () => {
    if (!googleMapRef.current) return;
    
    try {
      const center = googleMapRef.current.getCenter();
      const zoom = googleMapRef.current.getZoom();
      
      if (center && zoom) {
        const position = {
          center: { lat: center.lat(), lng: center.lng() },
          zoom
        };
        localStorage.setItem('mtmdMapPosition', JSON.stringify(position));
      }
    } catch (e) {
      // Ignore errors saving to localStorage
      console.warn('Failed to save map position:', e);
    }
  };

  // Get current user location
  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userPos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(userPos);
          
          // Center map on user location
          if (googleMapRef.current) {
            googleMapRef.current.setCenter(userPos);
            googleMapRef.current.setZoom(15);
          }
          
          setToast({
            type: 'success',
            message: 'Position trouvée'
          });
        },
        (error) => {
          logError('Geolocation error:', error);
          setToast({
            type: 'error',
            message: 'Impossible de trouver votre position'
          });
        }
      );
    } else {
      setToast({
        type: 'error',
        message: 'Géolocalisation non supportée par votre navigateur'
      });
    }
  };

  // Calculate route from addresses
  const calculateRoute = async (origin, waypoints, destination) => {
    if (!directionsServiceRef.current || !googleMapRef.current) {
      throw new Error('Services de direction non disponibles');
    }

    try {
      // Clear any existing WFS features
      clearWfsFeatures();
      
      // Format addresses to improve geocoding success
      const formatAddress = (address) => {
        // If it looks like coordinates, return as is
        if (/^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/.test(address)) {
          const [lat, lng] = address.split(',').map(coord => parseFloat(coord.trim()));
          return { lat, lng };
        }
        
        // Otherwise, ensure it's a complete address by adding region/country if missing
        if (!address.toLowerCase().includes('québec') && 
            !address.toLowerCase().includes('quebec') && 
            !address.toLowerCase().includes('qc')) {
          return `${address}, Québec, Canada`;
        }
        
        return address;
      };
      
      const formattedOrigin = formatAddress(origin);
      const formattedDestination = formatAddress(destination);
      const formattedWaypoints = waypoints.map(wp => formatAddress(wp));
      
      // Prepare waypoints for directions service
      const waypointsFormatted = formattedWaypoints.map(wp => ({
        location: wp,
        stopover: true
      }));

      // Request route from directions service
      const result = await directionsServiceRef.current.route({
        origin: formattedOrigin,
        destination: formattedDestination,
        waypoints: waypointsFormatted,
        travelMode: google.maps.TravelMode.DRIVING,
        optimizeWaypoints: true
      });

      // Display the route
      directionsRendererRef.current.setDirections(result);

      // Extract route points for WFS filtering
      const routePoints = result.routes[0].overview_path.map(point => ({
        lat: point.lat(),
        lng: point.lng()
      }));

      // Create a GeoJSON LineString from the route
      const routeLineString = turf.lineString(routePoints.map(p => [p.lng, p.lat]));
      
      // Store the current route
      setCurrentRoute(routeLineString);
      
      // Switch to WFS mode
      setDisplayMode('wfs');
      
      // Hide all WMS layers when in WFS mode
      Object.values(wmsLayersRef.current).forEach(layer => {
        if (layer && layer.setMap) {
          layer.setMap(null);
        }
      });
      
      // Fetch WFS features along the route
      await fetchWfsFeatures(routeLineString);

      // Show success message
      setToast({
        type: 'success',
        message: 'Itinéraire calculé avec succès'
      });

      return result;
    } catch (error) {
      logError('Failed to calculate route:', error);
      setToast({
        type: 'error',
        message: `Échec du calcul d'itinéraire: ${error.message || 'Erreur inconnue'}`
      });
      throw error;
    }
  };

  // Clear the current route
  const clearRoute = () => {
    if (directionsRendererRef.current) {
      directionsRendererRef.current.setDirections({ routes: [] });
    }
    
    // Clear WFS features
    clearWfsFeatures();
    
    // Reset state
    setCurrentRoute(null);
    setWfsFeatures([]);
    
    // Switch back to WMS mode
    setDisplayMode('wms');
    
    // Restore WMS layers
    layers.forEach(layer => {
      if (layer.enabled) {
        const wmsLayer = wmsLayersRef.current[layer.id];
        if (wmsLayer) {
          wmsLayer.setMap(googleMapRef.current);
        }
      }
    });
    
    // Show toast
    setToast({
      type: 'info',
      message: 'Itinéraire effacé'
    });
  };

  // Fetch WFS features along the route
  const fetchWfsFeatures = async (routeLineString) => {
    try {
      // Clear any existing WFS features first
      clearWfsFeatures();
      
      // Create a buffer around the route
      const buffered = turf.buffer(routeLineString, routeBuffer, { units: 'kilometers' });
      
      // Prepare WFS requests for each layer
      const enabledLayers = layers.filter(layer => layer.enabled);
      
      if (enabledLayers.length === 0) {
        setToast({
          type: 'warning',
          message: 'Aucune couche sélectionnée'
        });
        return;
      }
      
      // Show loading toast
      setToast({
        type: 'info',
        message: 'Chargement des données WFS...'
      });
      
      // Fetch features from Firestore for each enabled layer
      const layerResults = await Promise.all(enabledLayers.map(async layer => {
        try {
          logInfo(`Fetching features for layer ${layer.wfsName || layer.id} from Firestore`);
          
          // Get features from Firestore that intersect with the route
          const features = await getObjectsFromFirebase(layer.wfsName || layer.id, routeLineString, routeBuffer);
          
          return {
            layerId: layer.id,
            layerType: layer.id,
            layerName: layer.name,
            layerColor: layer.color,
            features: features || []
          };
        } catch (error) {
          logError(`Failed to fetch features for layer ${layer.id} from Firestore:`, error);
          return {
            layerId: layer.id,
            layerType: layer.id,
            layerName: layer.name,
            layerColor: layer.color,
            features: [],
            error: error.message
          };
        }
      }));
      
      // Count total features
      const totalFeatures = layerResults.reduce((sum, result) => sum + result.features.length, 0);
      
      // Log success for debugging
      logInfo(`Retrieved ${totalFeatures} features across ${layerResults.length} layers from Firestore`);
      
      // Display features on the map
      displayWfsFeatures(layerResults);
      
      // Store filtered features
      setWfsFeatures(layerResults);
      
      // Show success toast
      setToast({
        type: 'success',
        message: `${totalFeatures} objets trouvés le long de l'itinéraire`
      });
    } catch (error) {
      logError('Failed to fetch WFS features:', error);
      setToast({
        type: 'error',
        message: `Échec du chargement des données WFS: ${error.message}`
      });
    }
  };

  // Display WFS features on the map
  const displayWfsFeatures = (results) => {
    // Clear existing markers
    clearWfsFeatures();
    
    // Create new markers for each feature
    const newMarkers = [];
    
    results.forEach(result => {
      const { layerId, layerName, layerColor, features } = result;
      
      features.forEach(feature => {
        try {
          const geom = feature.geometry;
          if (!geom || !geom.coordinates) return;
          
          // Create marker based on geometry type
          let marker;
          
          switch (geom.type) {
            case 'Point': {
              marker = new google.maps.Marker({
                position: {
                  lat: geom.coordinates[1],
                  lng: geom.coordinates[0]
                },
                map: googleMapRef.current,
                title: feature.properties?.name || layerName,
                icon: {
                  path: google.maps.SymbolPath.CIRCLE,
                  fillColor: layerColor,
                  fillOpacity: 0.8,
                  strokeColor: '#FFFFFF',
                  strokeWeight: 1,
                  scale: 8
                }
              });
              
              // Add click listener for info window
              marker.addListener('click', () => {
                const infoWindow = new google.maps.InfoWindow({
                  content: createInfoWindowContent(feature, layerName, layerColor)
                });
                infoWindow.open(googleMapRef.current, marker);
              });
              
              marker.layerId = layerId;
              newMarkers.push(marker);
              break;
            }
            case 'LineString': {
              marker = new google.maps.Polyline({
                path: geom.coordinates.map(coord => ({
                  lat: coord[1],
                  lng: coord[0]
                })),
                map: googleMapRef.current,
                strokeColor: layerColor,
                strokeOpacity: 0.8,
                strokeWeight: 3
              });
              
              marker.layerId = layerId;
              newMarkers.push(marker);
              break;
            }
            case 'Polygon': {
              marker = new google.maps.Polygon({
                paths: geom.coordinates[0].map(coord => ({
                  lat: coord[1],
                  lng: coord[0]
                })),
                map: googleMapRef.current,
                strokeColor: layerColor,
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: layerColor,
                fillOpacity: 0.35
              });
              
              marker.layerId = layerId;
              newMarkers.push(marker);
              break;
            }
            default:
              break;
          }
        } catch (error) {
          logError('Error creating marker for feature:', error);
        }
      });
    });
    
    // Store markers for later cleanup
    wfsMarkersRef.current = newMarkers;
  };

  // Create info window content for a feature
  const createInfoWindowContent = (feature, layerName, layerColor) => {
    const props = feature.properties || {};
    
    // Create a div for the info window
    const div = document.createElement('div');
    div.className =  'p-2 max-w-xs';
    
    
    // Add title
    const title = document.createElement('h3');
    title.className = 'font-bold text-lg mb-2';
    title.style.color = layerColor;
    title.textContent = props.name || props.NOM || layerName || '';
    div.appendChild(title);
    
    // Add properties table
    const table = document.createElement('table');
    table.className = 'w-full text-sm';
    
    // Add table rows for each property
    Object.entries(props).forEach(([key, value]) => {
      // Skip internal or empty properties
      if (key.startsWith('_') || value === null || value === undefined || value === '') {
        return;
      }
      
      const row = document.createElement('tr');
      
      const keyCell = document.createElement('td');
      keyCell.className = 'font-medium pr-2';
      keyCell.textContent = key;
      row.appendChild(keyCell);
      
      const valueCell = document.createElement('td');
      valueCell.textContent = value;
      row.appendChild(valueCell);
      
      table.appendChild(row);
    });
    
    div.appendChild(table);
    
    return div;
  };

  // Clear WFS features from the map
  const clearWfsFeatures = () => {
    wfsMarkersRef.current.forEach(marker => {
      if (!marker) return;
      marker.setMap(null);
    });
    wfsMarkersRef.current = [];
  };

  // Update buffer distance
  const updateBufferDistance = (distance) => {
    setRouteBuffer(distance);
    
    // If we have a current route, refetch features with new buffer
    if (currentRoute) {
      fetchWfsFeatures(currentRoute);
    }
  };

  // Export current view as PNG
  const exportMapAsPNG = () => {
    if (!googleMapRef.current) return;

    try {
      // Create a canvas from the map
      const mapContainer = googleMapRef.current.getDiv();
      html2canvas(mapContainer).then(canvas => {
        // Convert canvas to PNG and download
        const link = document.createElement('a');
        link.download = `mtmd-map-${new Date().toISOString().slice(0, 10)}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        
        setToast({
          type: 'success',
          message: 'Carte exportée en PNG'
        });
      });
    } catch (error) {
      logError('Failed to export map:', error);
      setToast({
        type: 'error',
        message: 'Échec de l\'exportation'
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#3D3F42] flex flex-col">
      {/* Header */}
      <div className="bg-[#2A2B2E] p-4 shadow-md z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-700 rounded-lg text-white"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-semibold text-white">Carte MTMD</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setShowRouteBuilder(!showRouteBuilder)}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
              title="Planifier un itinéraire"
            >
              <Route className="w-4 h-4" />
              Itinéraire
            </Button>
            <Button
              onClick={() => setShowUpdateModal(true)}
              className="bg-gray-700 hover:bg-gray-600 text-white flex items-center gap-2"
              title="Mettre à jour les couches"
            >
              <RefreshCw className="w-4 h-4" />
              Mettre à jour
            </Button>
            <Button
              onClick={() => setShowHauteursLibresModal(true)}
              className="bg-gray-700 hover:bg-gray-600 text-white flex items-center gap-2"
              title="Hauteurs libres"
            >
              <Database className="w-4 h-4" />
              Hauteurs libres
            </Button>
            <Button
              onClick={() => setShowLegend(!showLegend)}
              className="bg-gray-700 hover:bg-gray-600 text-white flex items-center gap-2"
              title="Afficher la légende"
            >
              <Info className="w-4 h-4" />
              Légende
            </Button>
            <Button
              onClick={exportMapAsPNG}
              className="bg-gray-700 hover:bg-gray-600 text-white flex items-center gap-2"
              title="Exporter la carte"
            >
              <Download className="w-4 h-4" />
              Exporter
            </Button>
          </div>
        </div>
      </div>
      
      {/* Route Builder Panel */}
      {showRouteBuilder && (
        <div className="p-4 bg-[#2A2B2E] border-b border-gray-700">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-white">Planificateur d'itinéraire</h2>
              <div className="flex items-center gap-2">
                {currentRoute && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-300">Buffer (km):</label>
                    <select 
                      value={routeBuffer}
                      onChange={(e) => updateBufferDistance(Number(e.target.value))}
                      className="bg-gray-700 border border-gray-600 rounded text-white text-sm p-1"
                    >
                      <option value={0.1}>0.1</option>
                      <option value={0.5}>0.5</option>
                      <option value={1}>1</option>
                      <option value={2}>2</option>
                      <option value={5}>5</option>
                    </select>
                  </div>
                )}
                {currentRoute && (
                  <Button
                    onClick={clearRoute}
                    className="bg-red-600 hover:bg-red-700 text-white text-sm"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Effacer
                  </Button>
                )}
              </div>
            </div>
            
            <ItineraryRouteBuilder 
              onRouteCalculated={calculateRoute}
              onCancel={() => setShowRouteBuilder(false)}
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-1 relative">
        {/* Map Container */}
        <div className="flex-1 relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900/50 z-20">
              <LoadingSpinner />
              <div className="ml-3 text-white">Chargement de la carte...</div>
            </div>
          )}
          
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900/50 z-20">
              <div className="bg-red-500/10 text-red-400 p-6 rounded-lg max-w-md text-center">
                <h2 className="text-xl font-bold mb-2">Erreur</h2>
                <p>{error}</p>
                <Button
                  onClick={() => window.location.reload()}
                  className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2"
                >
                  Rafraîchir
                </Button>
              </div>
            </div>
          )}
          
          <div 
            ref={mapRef} 
            className="w-full h-full"
            style={{ minHeight: 'calc(100vh - 64px)', background: '#f0f0f0' }}
          ></div>
          
          {/* Map Controls */}
          <div className="absolute top-4 right-4 z-10 bg-[#2A2B2E]/90 backdrop-blur-sm p-4 rounded-lg shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-medium">
                {displayMode === 'wms' ? 'Couches MTMD' : 'Objets sur l\'itinéraire'}
              </h3>
              <Button
                onClick={getUserLocation}
                className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full"
                title="Ma position"
              >
                <MapIcon className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-2">
              {displayMode === 'wms' ? (
                // WMS Layer Controls
                layers.map(layer => (
                  <div key={layer.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`layer-${layer.id}`}
                      checked={layer.enabled}
                      onChange={() => toggleLayer(layer.id)}
                      className="rounded border-gray-600 text-red-600 focus:ring-red-500 bg-gray-700"
                    />
                    <label 
                      htmlFor={`layer-${layer.id}`}
                      className="flex items-center gap-2 text-white cursor-pointer"
                    >
                      <span 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: layer.color }}
                      ></span>
                      {layer.name}
                    </label>
                  </div>
                ))
              ) : (
                // WFS Feature Summary
                <>
                  <div className="text-sm text-white mb-2">
                    <strong>{wfsFeatures.reduce((sum, result) => sum + result.features.length, 0)}</strong> objets trouvés
                  </div>
                  {wfsFeatures.map(result => (
                    <div key={result.layerId} className="mb-2">
                      <div className="flex items-center gap-2 mb-1">
                        <span 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: result.layerColor }}
                        ></span>
                        <span className="text-white text-sm font-medium">{result.layerName}</span>
                      </div>
                      <div className="text-gray-400 text-xs ml-5">
                        <strong>{result.features.length}</strong> objets
                      </div>
                    </div>
                  ))}
                  {wfsFeatures.length === 0 && (
                    <div className="text-gray-400 text-sm">
                      Aucun objet trouvé. Essayez d'activer plus de couches ou d'augmenter la distance du buffer.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Legend Panel */}
        {showLegend && (
          <div className="absolute top-4 left-4 z-10 bg-[#2A2B2E]/90 backdrop-blur-sm p-4 rounded-lg shadow-lg max-w-xs">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-medium">Légende</h3>
              <button
                onClick={() => setShowLegend(false)}
                className="p-1 hover:bg-gray-700 rounded-full text-gray-400"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-4">
              {layers.map(layer => (
                <div key={`legend-${layer.id}`} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: layer.color }}
                    ></span>
                    <span className="text-white font-medium">{layer.name}</span>
                  </div>
                  <p className="text-xs text-gray-400 pl-5">{layer.description}</p>
                </div>
              ))}
              <div className="border-t border-gray-700 pt-2 mt-2">
                <p className="text-xs text-gray-400">
                  Données fournies par le Ministère des Transports et de la Mobilité durable du Québec
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast?.message || ""}
          type={toast?.type || "info"}
          onClose={() => setToast(null)}
        />
      )}

      {/* Layer Update Modal */}
      <LayerUpdateModal
        isOpen={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
      />

      {/* Hauteurs Libres Modal */}
      <HauteursLibresModal
        isOpen={showHauteursLibresModal}
        onClose={() => setShowHauteursLibresModal(false)}
      />
      
     {/* Remove API Key display */}
     <style>{`
       .gm-style-cc {
         display: none !important;
       }
       .gmnoprint.gm-style-cc {
         display: none !important;
       }
       .gm-style div[style*="bottom: 0px"] {
         display: none !important;
       }
       div[style*="background-color: rgba(0, 0, 0, 0.5)"] {
         display: none !important;
       }
       div[style*="color: rgb(255, 255, 255)"] {
         display: none !important;
       }
       .gm-style-pbc {
         display: none !important;
       }
       .gm-style div:has(> div > div > div:last-child:contains('Map Data')) {
         display: none !important;
       }
       .gm-style div:has(> div > div > div:last-child:contains('Google')) {
         display: none !important;
       }
       .gm-style > div:first-child > div:nth-child(1) > div:nth-child(3) > div:last-child {
         display: none !important;
       }
       .gm-style > div:first-child > div:nth-child(1) > div:nth-child(4) > div:last-child {
         display: none !important;
       }
     `}</style>
    </div>
  );
};

export default MTMDMapView;

export { MTMDMapView };